import { Link } from "react-router-dom";
import { bomMenus, camMenu, menus, ownerMenu } from "../utils";
import {
  actionsKeys,
  cn,
  permissionActions,
  ROLE_BOM,
  ROLE_CAM,
  ROLE_CAM_ACCOUNTANT,
  ROLE_OWNER,
} from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useState } from "react";
import { setInvoiceDueDate, setPendingBillingAmt } from "@/redux/slices";
import { ChevronDown, ChevronUp } from "lucide-react";
import { routes } from "@/routes/utils";

const Sidebar = ({ handleClose }: { handleClose?: () => void }) => {
  const [_menus, setMenus] = useState<any>(null);
  const dispatch = useDispatch();

  const isMobile = useIsMobile();
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const currentRole = useSelector(
    (state: RootState) => state.currentData.currentRole
  );
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  // Track the open state of the second-level sections by their title string names
  const [openSubSections, setOpenSubSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let menu = communityData?.bom && currentRole == ROLE_BOM
    ? bomMenus
    : currentRole == ROLE_OWNER
    ? ownerMenu
    : loggedInUserData?.roles?.includes(ROLE_CAM) ||
      loggedInUserData?.roles?.includes(ROLE_CAM_ACCOUNTANT)
    ? camMenu
    : menus;

    if (accessPermission) {
      if (currentRole == ROLE_BOM ||
          loggedInUserData?.roles?.includes(ROLE_CAM) ||
          loggedInUserData?.roles?.includes(ROLE_CAM_ACCOUNTANT)
      ) {
        const hasReadMasterPermission = accessPermission?.[actionsKeys?.MASTER_DATA?.toLowerCase()]?.[permissionActions.READ];
  
        if (!hasReadMasterPermission) {
          menu = menu?.filter((data: any) => !data?.to?.includes(routes.masterData))
        }
      }
    }

    setMenus(menu);
  }, [communityData, currentRole, accessPermission]);

  const toggleSubSection = (title: string) => {
    setOpenSubSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div className="h-[calc(100vh-51px)] overflow-y-scroll hide-scroll-y overflow-x-hidden">
      {_menus?.map((menu: any) => {
        // Deep verification to see if current path matches parent L1, L2 children, or nested L3 subchildren
        const isActive =
          location.pathname === menu.to ||
          location.pathname.startsWith(menu.to + "/") ||
          menu.children?.some((child: any) => 
            location.pathname === child.to || 
            location.pathname.startsWith(child.to + "/") ||
            child.children?.some((subChild: any) => location.pathname === subChild.to || location.pathname.startsWith(subChild.to + "/"))
          );

        if (menu.children && menu.children.length > 0) {
          return (
            <>
              <Link
                to={menu.to || ""}
                key={menu.title}
                onClick={() => {
                  dispatch(setInvoiceDueDate(null));
                  dispatch(setPendingBillingAmt("-1"));
                  handleClose?.();
                }}
                className={cn(
                  "flex relative  group transition-all text-[14px] font-[500]",
                  isActive
                    ? "text-primary"
                    : "text-gray-500 hover:text-primary",
                  isMobile
                    ? "w-full p-[5px] mb-[10px] flex-row items-start gap-2"
                    : "px-[10px] py-[15px] mb-[10px] flex-col items-center justify-center"
                )}
              >
                {/* Active Indicator */}
                {!isMobile && (
                  <div
                    className={cn(
                      "w-1 h-full bg-primary absolute rounded-full left-0 transition-opacity",
                      isActive
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    )}
                  ></div>
                )}
                <menu.icon
                  className={cn(
                    "xl:h-auto xl:w-auto",
                    isActive
                      ? "stroke-primary text-primary"
                      : "stroke-g-300 text-g-300 group-hover:text-primary group-hover:stroke-primary"
                  )}
                />
                <span
                  className={
                    isActive
                      ? "text-primary"
                      : "group-hover:text-primary text-g-300"
                  }
                >
                  {menu.title}
                </span>
              </Link>
              <div
                className={cn(
                  "absolute top-0 bg-white h-full px-[15px] py-[20px] w-[200px] rounded-r-lg transition-all duration-300 ease-in-out overflow-y-scroll hide-scroll-y pb-[100px]",
                  isActive
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-5 pointer-events-none",
                  isMobile ? "left-[180px]" : "left-[117px]"
                )}
              >
                <div className="flex flex-col gap-2">
                  {menu.children.map((child: any) => {
                    const hasSubChildren = child.children && child.children.length > 0;
                    const isSectionOpen = !!openSubSections[child.title];

                    // Check if current page path matches the L2 container or any hidden nested L3 layout links
                    const isChildActive =
                      location.pathname === child.to ||
                      location.pathname.startsWith(child.to + "/") ||
                      child.children?.some((subChild: any) => location.pathname === subChild.to);

                    if (hasSubChildren) {
                      return (
                        <div key={child.title} className={cn("flex flex-col w-full border-b-1", isSectionOpen ? "bg-blue-100 rounded-sm" : "")}>
                          {/* Level 2 Collapsible Header Block */}
                          <button
                            type="button"
                            onClick={() => toggleSubSection(child.title)}
                            className={cn(
                              "text-[14px] font-[500] px-2 py-1 rounded-md text-left flex justify-between items-center w-full",
                              isSectionOpen ? "text-primary" : "text-g-300 hover:text-white hover:bg-primary"
                            )}
                          >
                            <span>{child.title}</span>
                            <span className="text-[10px]">{isSectionOpen ? <ChevronUp className="h-5" /> : <ChevronDown className="h-5" />}</span>
                          </button>

                          {/* Level 3 Nav Actions Links mapping block context wrapper */}
                          {isSectionOpen && (
                            <div className="flex flex-col gap-1 mt-1 ml-1">
                              {child.children.map((subChild: any) => {
                                const isSubChildActive = location.pathname === subChild.to || location.pathname.startsWith(subChild.to + "/");
                                return (
                                  <Link
                                    to={subChild.to}
                                    key={subChild.title}
                                    onClick={handleClose}
                                    className={cn(
                                      "text-[13px] font-thin px-2 py-1 rounded-md flex items-center justify-between",
                                      isSubChildActive
                                        ? "bg-primary text-white"
                                        : "text-black hover:text-white hover:bg-primary"
                                    )}
                                  >
                                    <span>{subChild.title}</span>
                                    {isSubChildActive && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Standard Level 2 fallback link if it has no 3rd tier arrays
                    return (
                      <Link
                        to={child.to}
                        key={child.title}
                        onClick={handleClose}
                        className={cn(
                          "text-[14px] font-[500] px-2 py-1 rounded-md",
                          isChildActive
                            ? "bg-primary text-white"
                            : "text-g-300 hover:text-white hover:bg-primary"
                        )}
                      >
                        {child.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </>
          );
        } else if (menu.to) {
          return (
            <Link
              to={menu.to}
              key={menu.title}
              onClick={() => {
                dispatch(setInvoiceDueDate(null));
                dispatch(setPendingBillingAmt("-1"));
                handleClose?.();
              }}
              className={cn(
                "flex relative  group transition-all text-[14px] font-[500]",
                isActive ? "text-primary" : "text-gray-500 hover:text-primary",
                isMobile
                  ? "w-full p-[5px] mb-[10px] flex-row items-start gap-2"
                  : "px-[10px] py-[15px] mb-[10px] flex-col items-center justify-center w-[120px]"
              )}
            >
              {/* Active Indicator */}
              {!isMobile && (
                <div
                  className={cn(
                    "w-1 h-full bg-primary absolute rounded-full left-0 transition-opacity",
                    isActive
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100"
                  )}
                ></div>
              )}
              <menu.icon
                className={cn(
                  "xl:h-auto xl:w-auto",
                  isActive
                    ? "stroke-primary text-primary"
                    : "stroke-g-300 text-g-300 group-hover:text-primary group-hover:stroke-primary"
                )}
              />
              <span
                className={
                  isActive
                    ? "text-primary text-center"
                    : "group-hover:text-primary text-g-300 text-center"
                }
              >
                {menu.title}
              </span>
            </Link>
          );
        }
      })}
    </div>
  );
};

export default Sidebar;